import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, RefreshCw, Loader2, CheckCircle2, AlertTriangle, ExternalLink, Send, Inbox } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Whether outreach actually leaves the real Microsoft 365 mailboxes. The three
   Entra values are deployment secrets, so nothing here accepts or shows one —
   it reports what the token is granted and which mailboxes will let us in. */
export default function MicrosoftMailCard() {
  const [h, setH] = useState(null);
  const [boxes, setBoxes] = useState([]);
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState("");
  const [polling, setPolling] = useState(false);

  const loadRun = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/outreach/replies/last-run`);
      setRun(data.run || null);
    } catch { setRun(null); }
  }, []);

  const pollReplies = async () => {
    setPolling(true);
    try {
      const { data } = await axios.post(`${API}/outreach/replies/poll`);
      if (!data.ran) { toast.error(data.error || "Reading mail needs Microsoft 365"); return; }
      const kept = (data.prospect_replies || 0) + (data.rep_replies || 0)
        + (data.inbound_enquiries || 0);
      toast.success(kept ? `${kept} message${kept === 1 ? "" : "s"} filed` : "Nothing new to file",
        { description: [
            data.prospect_replies ? `${data.prospect_replies} from them.` : "",
            data.rep_replies ? `${data.rep_replies} sent by a rep from Outlook.` : "",
            data.inbound_enquiries ? `${data.inbound_enquiries} new enquir${data.inbound_enquiries === 1 ? "y" : "ies"}.` : "",
            data.paused?.length ? `Outreach stopped to ${data.paused.join(", ")}.` : "",
            data.bounces ? `${data.bounces} bounced.` : "",
            data.auto_replies ? `${data.auto_replies} out of office.` : "",
          ].filter(Boolean).join(" ") || undefined });
      setRun(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not read the mailboxes");
    } finally { setPolling(false); }
  };

  const load = useCallback(async (loud = false) => {
    setLoading(true);
    try {
      const [health, mailboxes] = await Promise.all([
        axios.get(`${API}/outreach/graph/health`),
        axios.get(`${API}/outreach/mailboxes`),
      ]);
      setH(health.data);
      setBoxes(mailboxes.data.mailboxes || []);
      if (loud) {
        health.data.ok ? toast.success("Microsoft 365 is ready to send")
          : toast.error(health.data.error || "Microsoft 365 is not ready yet");
      }
    } catch (e) {
      setH({ ok: false, error: e?.response?.data?.detail || "The check failed" });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(false); loadRun(); }, [load, loadRun]);

  const testSend = async (email) => {
    setTesting(email);
    try {
      const { data } = await axios.post(`${API}/outreach/graph/test-send`, { from_mailbox: email });
      toast.success(`Test sent as ${data.from} to ${data.to} via ` +
        `${data.provider === "graph" ? "Microsoft 365" : "Resend"}`, {
        description: data.saved_to_sent_items
          ? "Check the inbox, and check Sent Items of the sending mailbox."
          : "It went through Resend, so there will be no copy in Sent Items.",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The test send failed");
    } finally { setTesting(""); }
  };

  const ok = !!h?.ok;
  const graph = h?.provider === "graph";

  return (
    <Card className="bg-white border-[#DAD5CA]" data-testid="microsoft-mail-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#59554D]" />
            <div>
              <h3 className="text-[15px] font-bold text-[#1A1A1A]">Microsoft 365 — outgoing mail</h3>
              <p className="text-[12.5px] text-[#59554D]">
                Anything the app sends as a person — outreach, quote emails, prospect and client
                emails — leaves that person's own mailbox, so Microsoft keeps the copy in their
                Outlook Sent Items and replies thread normally. System mail from noreply@ still
                goes through Resend, because it is not a mailbox.
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={() => load(true)} disabled={loading}
            className="h-8 border-[#DAD5CA] text-[#59554D] shrink-0" data-testid="ms-mail-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="ms-mail-state">
          {ok ? (
            <span className="inline-flex items-center gap-1.5 mono text-[11px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: "#E3F3E9", border: "1px solid #A9CFBB", color: "#0E6B3F" }}>
              <CheckCircle2 className="w-3 h-3" /> SENDING VIA MICROSOFT 365
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 mono text-[11px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: "#FFF8D6", border: "1px solid #E8C86A", color: "#6B5600" }}>
              <AlertTriangle className="w-3 h-3" /> {graph ? "NOT WORKING YET" : "FALLING BACK TO RESEND"}
            </span>
          )}
          {h?.roles?.length > 0 && (
            <span className="text-[11.5px] mono text-[#8A857A]" data-testid="ms-mail-roles">
              granted: {h.roles.join(", ")}
            </span>
          )}
        </div>

        {!ok && h?.error && (
          <div className="mt-2 rounded-lg border border-[#E8C86A] bg-[#FFF8D6] p-2.5" data-testid="ms-mail-detail">
            <p className="text-[12.5px] text-[#6B5600]">{h.error}</p>
            {h?.consent_url && (
              <a href={h.consent_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-[12px] font-semibold text-[#6B5600] underline"
                data-testid="ms-mail-consent-link">
                Open this app's API permissions in Entra <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        )}

        {(h?.mailboxes || []).length > 0 && (
          <div className="mt-3">
            {h.mailboxes.map((m) => (
              <div key={m.email} className="flex items-baseline gap-2 py-1 border-b border-[#F4F2ED] last:border-0"
                data-testid={`ms-mail-box-${m.email}`}>
                <span className="text-[11px] uppercase tracking-[0.08em] text-[#8A857A] w-[150px] shrink-0">
                  Mailbox
                </span>
                <span className="text-[12.5px] break-all" style={{ color: m.ok ? "#0E6B3F" : "#8A6D00" }}>
                  {m.ok ? "✓" : "✕"} {m.email}{m.ok ? "" : ` — ${m.error}`}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-[#F4F2ED]">
          <p className="text-[12px] text-[#8A857A] mb-2">
            Every 15 minutes both folders of these mailboxes are read: their replies from the
            Inbox, and the replies a rep typed in Outlook from Sent Items, so a prospect's
            timeline reads as the conversation it actually was. A reply either way stops
            outreach to that prospect until a person lifts it. Nothing is sent, deleted or
            marked as read. A test send goes to you, never to a prospect.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={polling} onClick={pollReplies}
              data-testid="ms-mail-poll-replies" className="h-8 border-[#DAD5CA] text-[#59554D]">
              {polling ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                : <Inbox className="w-3.5 h-3.5 mr-1.5" />}
              Check for mail now
            </Button>
            {boxes.filter((m) => m.email).map((m) => (
              <Button key={m.email} size="sm" variant="outline" disabled={!!testing}
                onClick={() => testSend(m.email)} data-testid={`ms-mail-test-${m.email}`}
                className="h-8 border-[#DAD5CA] text-[#59554D]">
                {testing === m.email
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Test as {m.name || m.email}
              </Button>
            ))}
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-[#F4F2ED]" data-testid="ms-mail-last-run">
          <div className="flex items-baseline justify-between gap-2 mb-1.5">
            <span className="mono text-[10.5px] uppercase tracking-[0.08em] text-[#8A857A]">
              Last capture run
            </span>
            <span className="mono text-[11.5px] text-[#59554D]" data-testid="ms-mail-last-run-at">
              {run?.ran_at ? new Date(run.ran_at).toLocaleString() : "has not run yet"}
            </span>
          </div>
          {!run ? (
            <p className="text-[12px] text-[#8A857A]">
              Nothing recorded yet. It runs on its own every 15 minutes, or press Check for mail
              now.
            </p>
          ) : (
            <>
              <p className="text-[12px] text-[#59554D]" data-testid="ms-mail-run-totals">
                {run.looked_at || 0} message{run.looked_at === 1 ? "" : "s"} looked at ·{" "}
                {run.prospect_replies || 0} from them · {run.rep_replies || 0} sent by a rep ·{" "}
                {run.inbound_enquiries || 0} new enquir{run.inbound_enquiries === 1 ? "y" : "ies"} ·{" "}
                {run.auto_replies || 0} out of office · {run.bounces || 0} bounced ·{" "}
                {run.discarded || 0} nothing to do with us
              </p>
              {(run.mailboxes || []).map((m) => (
                <div key={m.mailbox} className="flex items-baseline gap-2 py-1 border-b border-[#F4F2ED] last:border-0"
                  data-testid={`ms-mail-run-${m.mailbox}`}>
                  <span className="text-[12px] text-[#1A1A1A] w-[210px] shrink-0 break-all">{m.mailbox}</span>
                  <span className="mono text-[11.5px] text-[#59554D]">
                    {m.looked_at || 0} looked at · {m.prospect_replies || 0} replies ·{" "}
                    {m.rep_replies || 0} rep replies · {m.inbound_enquiries || 0} enquiries ·{" "}
                    {m.discarded || 0} discarded
                    {m.duplicates ? ` · ${m.duplicates} already filed` : ""}
                  </span>
                </div>
              ))}
              {(run.errors || []).map((e) => (
                <div key={e.mailbox} className="mt-1.5 rounded-lg border border-[#E8C86A] bg-[#FFF8D6] p-2"
                  data-testid={`ms-mail-run-error-${e.mailbox}`}>
                  <p className="text-[12px] text-[#6B5600]">
                    Microsoft would not let us read {e.mailbox} — {e.error}
                  </p>
                </div>
              ))}
              {run.paused?.length > 0 && (
                <p className="text-[12px] text-[#0E6B3F] mt-1.5" data-testid="ms-mail-run-paused">
                  Outreach stopped to {run.paused.join(", ")}.
                </p>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
